/**
 * Bridges Tiptap suggestion plugins to the React SuggestionMenu. Ported
 * from Loop's suggestionRenderer, minus tippy: the CRM doesn't ship
 * tippy.js, so the menu mounts in a position:fixed host on body,
 * anchored from the suggestion plugin's `clientRect` (viewport coords —
 * same trick as the "+fields" chip). Flips above the caret when the
 * bottom edge would clip.
 */
import { createRoot, type Root } from "react-dom/client";
import { createRef, type RefObject } from "react";
import {
    SuggestionMenu,
    type SuggestionItem,
    type SuggestionMenuRef,
} from "./SuggestionMenu";

export interface SuggestionLifecycleProps<TItem> {
    items: TItem[];
    command: (item: TItem) => void;
    clientRect?: (() => DOMRect | null) | null;
}

const MENU_WIDTH = 280;
const MENU_MAX_HEIGHT = 288;

export function createSuggestionRenderer<TItem>({
    label,
    toItem,
}: {
    label: string;
    toItem: (item: TItem) => SuggestionItem;
}) {
    let host: HTMLDivElement | null = null;
    let root: Root | null = null;
    const menuRef: RefObject<SuggestionMenuRef | null> = createRef();
    let currentProps: SuggestionLifecycleProps<TItem> | null = null;

    const position = (clientRect?: (() => DOMRect | null) | null) => {
        if (!host) return;
        const rect = clientRect?.() ?? null;
        if (!rect) {
            host.style.display = "none";
            return;
        }
        host.style.display = "";
        host.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8))}px`;
        if (rect.bottom + 6 + MENU_MAX_HEIGHT > window.innerHeight) {
            host.style.top = "auto";
            host.style.bottom = `${window.innerHeight - rect.top + 6}px`;
        } else {
            host.style.top = `${rect.bottom + 6}px`;
            host.style.bottom = "auto";
        }
    };

    const renderMenu = (props: SuggestionLifecycleProps<TItem>) => {
        if (!root) return;
        currentProps = props;
        root.render(
            <SuggestionMenu
                ref={menuRef}
                label={label}
                items={props.items.map(toItem)}
                onSelect={(idx) => {
                    const picked = props.items[idx];
                    if (picked !== undefined) props.command(picked);
                }}
            />,
        );
    };

    return {
        onStart: (props: SuggestionLifecycleProps<TItem>) => {
            host = document.createElement("div");
            host.className = "loop-sugg-host";
            document.body.appendChild(host);
            root = createRoot(host);
            renderMenu(props);
            position(props.clientRect);
        },
        onUpdate: (props: SuggestionLifecycleProps<TItem>) => {
            renderMenu(props);
            position(props.clientRect);
        },
        onKeyDown: ({ event }: { event: KeyboardEvent }) => {
            if (event.key === "Escape") {
                if (host) host.style.display = "none";
                return true;
            }
            if (currentProps && currentProps.items.length === 0) return false;
            return menuRef.current?.onKeyDown(event) ?? false;
        },
        onExit: () => {
            // Defer unmount so React can finish in-flight updates from the
            // same transaction that closed the menu.
            const r = root;
            const h = host;
            queueMicrotask(() => {
                r?.unmount();
                h?.remove();
            });
            root = null;
            host = null;
            currentProps = null;
        },
    };
}
